import { useApolloClient } from "@apollo/client";
import { getDataOrNull, zipOrThrow } from "@msys/common";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { ProductSearchItem__ProductSearchResultFragment } from "../../features/products/Product.generated.js";
import { ProductPreferenceFragment } from "../../features/products/ProductPreferences.generated.js";
import { articleNumberIdentifierKey } from "../../features/products/productPreferences.js";
import {
  ProductPreferencesPage_PreferredProductDocument,
  ProductPreferencesPage_PreferredProductQuery,
  ProductPreferencesPage_PreferredProductQueryVariables,
} from "./ProductPreferences.generated.js";

type ArticleNumberIdentifier = { supplierId: string; articleNumber: string };

type PreferenceAttributions =
  ProductSearchItem__ProductSearchResultFragment["preferences"];

export type PreferredProduct = NonNullable<
  Extract<
    ProductPreferencesPage_PreferredProductQuery["pimGetProduct"],
    { __typename: "PimGetProductResult" }
  >["product"]
>;

/**
 * The products behind a product type's preferences, in the shape the product list views
 * render.
 *
 * A preference stores only `(supplierId, articleNumber)` and PIM offers no by-identifier
 * product search, so each article is fetched by identifier and adapted into a search result.
 * An article the catalogue no longer returns is kept as a row carrying just its identifier:
 * dropping it would leave a preference the user can neither see nor remove.
 */
export function usePreferredProducts(
  preferences: readonly ProductPreferenceFragment[]
) {
  const client = useApolloClient();

  const identifiers = React.useMemo(() => {
    const byArticle = new Map<string, ArticleNumberIdentifier>();
    for (const preference of preferences) {
      const { supplierId, articleNumber } = preference.articleNumberIdentifier;
      // One row per article: an article preferred at both scopes is one product.
      byArticle.set(articleNumberIdentifierKey({ supplierId, articleNumber }), {
        supplierId,
        articleNumber,
      });
    }
    return [...byArticle.values()];
  }, [preferences]);

  const [state, setState] = React.useState<{
    fetching: boolean;
    products: ProductSearchItem__ProductSearchResultFragment[];
  }>({ fetching: identifiers.length > 0, products: [] });

  React.useEffect(() => {
    let cancelled = false;

    if (identifiers.length === 0) {
      setState({ fetching: false, products: [] });
      return;
    }

    // The products already fetched stay on screen while the new set loads: a preference write
    // refetches this list, and emptying it in between makes the list flicker.
    setState(current => ({ ...current, fetching: true }));

    Promise.allSettled(
      identifiers.map(async identifier => {
        const result = await client.query<
          ProductPreferencesPage_PreferredProductQuery,
          ProductPreferencesPage_PreferredProductQueryVariables
        >({
          query: ProductPreferencesPage_PreferredProductDocument,
          variables: identifier,
        });
        return getDataOrNull(result.data.pimGetProduct)?.product ?? null;
      })
    ).then(results => {
      if (cancelled) return;
      // A failed lookup degrades to the identifier-only row, so the rest of the list stays
      // visible and the failed preference stays removable.
      const products = zipOrThrow(identifiers, results).map(
        ([identifier, result]) =>
          result.status === "fulfilled" && result.value !== null
            ? toProductSearchResult(result.value)
            : toUnresolvedProductSearchResult(identifier)
      );
      setState({ fetching: false, products });
      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (failure?.reason instanceof Error)
        enqueueSnackbar(failure.reason.message, { variant: "error" });
    });

    return () => {
      cancelled = true;
    };
  }, [client, identifiers]);

  // The attribution a search result carries, per article. Derived from the preference list
  // rather than fetched, because the product query has no `preferences` field to select — it
  // exists only on the search result types.
  const attributionsByArticle = React.useMemo(() => {
    const byArticle = new Map<string, PreferenceAttributions>();
    for (const preference of preferences) {
      const key = articleNumberIdentifierKey(
        preference.articleNumberIdentifier
      );
      byArticle.set(key, [
        ...(byArticle.get(key) ?? []),
        {
          __typename: "PimProductPreferenceAttribution",
          scope: preference.scope,
          productType: preference.productType,
        },
      ]);
    }
    return byArticle;
  }, [preferences]);

  // Removals apply at once — the preference is gone, so its row is — while an addition waits
  // for its product. Kept in sync here rather than by blanking the list on every write, and
  // the attribution is stamped in the same pass so a scope change shows without a refetch.
  const products = React.useMemo(() => {
    const wanted = new Set(identifiers.map(articleNumberIdentifierKey));
    return state.products
      .filter(product => wanted.has(articleNumberIdentifierKey(product)))
      .map(product => ({
        ...product,
        preferences:
          attributionsByArticle.get(articleNumberIdentifierKey(product)) ?? [],
      }));
  }, [state.products, identifiers, attributionsByArticle]);

  return {
    // Nothing to show yet is the only state worth a spinner.
    loading: state.fetching && products.length === 0,
    products,
  };
}

export function toProductSearchResult(
  product: PreferredProduct
): ProductSearchItem__ProductSearchResultFragment {
  const shared = {
    // The attribution is not on the product — it is stamped from the preference list the
    // hook already holds, so it stays in step with a write instead of with the last fetch.
    preferences: [],
    id: product.id,
    articleNumber: product.articleNumber,
    gtin: product.gtin,
    supplierId: product.supplierId,
    supplier: product.supplier,
    texts: product.texts,
    attachments: product.attachments,
    pricing: product.pricing,
    status: product.status,
    organisationFavourite: product.organisationFavourite,
    manufacturerArticleNumber: product.manufacturerArticleNumber,
    // The search result flattens the branding to the brand's name.
    brand: product.branding?.brand.title ?? null,
    // The product query exposes the classification as PimProductTypeRef, whose label is
    // the search result's PimProductTypeMinimal title.
    productType: product.productType
      ? {
          __typename: "PimProductTypeMinimal" as const,
          id: product.productType.id,
          key: product.productType.key,
          title: product.productType.label,
          productTypeSystemKey: product.productType.productTypeSystemKey,
        }
      : null,
  };

  return product.__typename === "PimSupplierProduct"
    ? {
        __typename: "PimSupplierProductSearchResult",
        ...shared,
        supplierFavourite: product.supplierFavourite,
      }
    : { __typename: "PimCustomSupplierProductSearchResult", ...shared };
}

export function toUnresolvedProductSearchResult(
  identifier: ArticleNumberIdentifier
): ProductSearchItem__ProductSearchResultFragment {
  return {
    __typename: "PimCustomSupplierProductSearchResult",
    preferences: [],
    id: articleNumberIdentifierKey(identifier),
    articleNumber: identifier.articleNumber,
    gtin: null,
    supplierId: identifier.supplierId,
    supplier: {
      __typename: "PimSupplier",
      id: identifier.supplierId,
      title: "",
      logo: null,
    },
    texts: {
      __typename: "PimProductTexts",
      title: identifier.articleNumber,
      description: null,
      extendedDescription: null,
    },
    attachments: [],
    pricing: null,
    status: null,
    organisationFavourite: false,
    manufacturerArticleNumber: null,
    brand: null,
    productType: null,
  };
}
